import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Bus, Users, IndianRupee, TicketCheck, Calendar as CalIcon, XCircle, Clock, AlertTriangle, ShieldAlert, FileText, Languages } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon:Icon, tone="indigo" }) => {
  const tones = { indigo:"bg-[#1e1b4b] text-white", orange:"bg-[#ea580c] text-white", white:"bg-white text-[#1e1b4b] border border-slate-200" };
  return (
    <div className={`p-5 rounded-2xl ${tones[tone]}`} data-testid={`stat-${label.replace(/\s/g,'-').toLowerCase()}`}>
      <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-widest opacity-80">{label}</div><Icon className="w-5 h-5 opacity-80" /></div>
      <div className="text-2xl md:text-3xl font-display font-black mt-2">{value}</div>
    </div>
  );
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [alerts, setAlerts] = useState([]);
  useEffect(()=>{
    api.get("/admin/dashboard").then(r=>setD(r.data)).catch(()=>{});
    api.get("/admin/alerts").then(r=>setAlerts(r.data)).catch(()=>{});
  }, []);
  if (!d) return <div>Loading…</div>;
  const sev = (s) => s==="expired"?"bg-red-100 text-red-800 border-red-200": s==="urgent"?"bg-orange-100 text-orange-800 border-orange-200":"bg-amber-50 text-amber-800 border-amber-200";
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">Today · {new Date().toLocaleDateString()}</div>
          <h1 className="text-2xl md:text-3xl font-display font-black tracking-tight">Operations Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/bookings" className="px-3 py-2 rounded-md bg-[#1e1b4b] text-white text-sm font-semibold" data-testid="qa-bookings">All Bookings</Link>
          <Link to="/admin/calendar" className="px-3 py-2 rounded-md border border-slate-300 text-sm font-semibold hover:bg-white" data-testid="qa-calendar">Calendar</Link>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="mt-6 p-5 rounded-2xl bg-white border border-orange-200" data-testid="alerts-panel">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#ea580c]" />
            <div className="font-display font-bold">Document Alerts · {alerts.length} vehicle/driver document(s) expiring soon</div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {alerts.map((a,i)=>(
              <div key={i} className={`p-3 rounded-lg border text-xs ${sev(a.severity)}`} data-testid={`alert-${i}`}>
                <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {a.document}</div>
                <div className="mt-1">{a.vehicle_number || a.driver_name}</div>
                <div className="mt-1">Expires {a.expires_on} · {a.days_left < 0 ? `${Math.abs(a.days_left)} days overdue` : `${a.days_left} days left`}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-6">
        <Stat label="Today Bookings" value={d.today_bookings} icon={TicketCheck} tone="indigo" />
        <Stat label="Revenue Today" value={`₹${d.revenue_today.toLocaleString()}`} icon={IndianRupee} tone="orange" />
        <Stat label="Vehicles Avail" value={`${d.vehicles_available}/${d.vehicles_total}`} icon={Bus} tone="white" />
        <Stat label="Drivers Avail" value={d.drivers_available} icon={Users} tone="white" />
        <Stat label="Total Bookings" value={d.total_bookings} icon={CalIcon} tone="white" />
        <Stat label="Pending Pay" value={d.pending_payments} icon={Clock} tone="white" />
        <Stat label="Cancelled" value={d.cancelled} icon={XCircle} tone="white" />
        <Stat label="Total Revenue" value={`₹${d.total_revenue.toLocaleString()}`} icon={IndianRupee} tone="indigo" />
      </div>
      <div className="mt-8 p-4 md:p-6 rounded-2xl bg-white border border-slate-200">
        <div className="font-display font-bold text-lg">Recent Bookings</div>
        <div className="mt-4 divide-y divide-slate-100">
          {d.recent_bookings.map(b => (
            <div key={b.booking_id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm" data-testid={`recent-${b.booking_id}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{b.customer_name} · <span className="text-slate-500">{b.customer_mobile}</span></div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{b.booking_id} · {b.from_place} → {b.to_place} · {b.travel_date}</div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 capitalize">{b.status.replace(/_/g,' ')}</div>
                <div className="text-sm font-black text-[#ea580c]">₹{b.fare.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 p-4 md:p-6 rounded-2xl bg-white border border-slate-200" data-testid="admin-guides-card">
        <div className="font-display font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#ea580c]" /> Booking Flow Guides</div>
        <p className="text-xs text-slate-500 mt-1">Step-by-step guide for Customer / Driver / Admin. Open, then Print → Save as PDF from your browser to hand out.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <a href="/docs/booking-guide-en.html" target="_blank" rel="noreferrer" className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center gap-3 transition" data-testid="guide-en-link">
            <div className="w-10 h-10 rounded-lg bg-[#ea580c] text-white grid place-items-center font-black">EN</div>
            <div className="min-w-0">
              <div className="font-display font-bold text-[#1e1b4b]">English Guide</div>
              <div className="text-xs text-slate-500">Booking journey · Roles · Cheat-sheets</div>
            </div>
          </a>
          <a href="/docs/booking-guide-mr.html" target="_blank" rel="noreferrer" className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center gap-3 transition" data-testid="guide-mr-link">
            <div className="w-10 h-10 rounded-lg bg-[#1e1b4b] text-white grid place-items-center"><Languages className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="font-display font-bold text-[#1e1b4b]">मराठी मार्गदर्शक</div>
              <div className="text-xs text-slate-500">बुकिंग प्रवास · भूमिका · चीट-शीट</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
